/**
 * RangeSlider formatting + label contract (currency-aware).
 *
 * Pure (non-component) helpers shared between RangeSlider and the SortFilterBar
 * active-chip bar so a slider and its summary chip never disagree. Moved here
 * from `components/ui/range-slider-format.ts` so the money path goes through the
 * one currency lib: `money` endpoints now apply the exchange rate and use the
 * active currency symbol (was hard-coded `$`, no conversion).
 *
 * The `≤` / `≥` / `–` operators are visual constants, not translatable.
 */

export type RangeFormat = 'money' | 'int' | 'pct' | 'count';

export interface RangeFormatSpec {
  format: RangeFormat;
  /** pct only: prefix positive values with `+` (e.g. 24h price change). */
  signed?: boolean;
  /** count/int only: appended unit suffix (e.g. "outcomes"). */
  unit?: string;
  /** i18n override for the "Any" value-label. */
  anyLabel?: string;
  /** money only: USDC→local exchange rate (slider values are whole USD). */
  rate?: number;
  /** money only: active currency symbol (e.g. "€", "₹"). Defaults to "$". */
  symbol?: string;
}

function isOpenEnded(format: RangeFormat): boolean {
  return format === 'money' || format === 'int' || format === 'count';
}

export const MILLION = 1e6;
export const THOUSAND = 1e3;
export const EPSILON = 1e-6;

/** Strip a trailing `.0` from an abbreviated value (`1.0M` → `1M`). */
function trim(n: number): string {
  return String(n).replace(/\.0$/, '');
}

/**
 * Format a money endpoint: convert the whole-USD slider value to local currency
 * and prefix the active symbol. `860k`, `1.2M`, etc.
 */
function formatMoney(value: number, spec: RangeFormatSpec): string {
  const symbol = spec.symbol ?? '$';
  const rate = spec.rate !== undefined && spec.rate > 0 ? spec.rate : 1;
  const local = value * rate;
  const abs = Math.abs(local);
  if (abs >= MILLION) return `${symbol}${trim(Math.round((local / MILLION) * 10) / 10)}M`;
  if (abs >= THOUSAND) return `${symbol}${trim(Math.round((local / THOUSAND) * 10) / 10)}k`;
  return `${symbol}${String(Math.round(local))}`;
}

/** Format a single endpoint value in the dimension's native units. */
export function formatRangeEndpoint(value: number, spec: RangeFormatSpec): string {
  const rounded = Math.round(value);
  switch (spec.format) {
    case 'money':
      return formatMoney(rounded, spec);
    case 'pct':
      return `${spec.signed === true && rounded > 0 ? '+' : ''}${rounded}%`;
    default:
      return String(rounded);
  }
}

/**
 * The `Any · ≤ · ≥ · range` value label, operating on native values.
 * - both thumbs at the ends → `Any`
 * - only low at min → `≤ <hi>` (open upper renders `<hi>+` at max)
 * - only high at max → `≥ <lo>`
 * - otherwise → `<lo> – <hi>`
 */
export function formatRangeValueLabel(
  lo: number,
  hi: number,
  min: number,
  max: number,
  spec: RangeFormatSpec,
): string {
  const atMin = lo <= min + EPSILON;
  const atMax = hi >= max - EPSILON;
  if (atMin && atMax) return spec.anyLabel ?? 'Any';

  const unit = spec.unit !== undefined && spec.unit !== '' ? ` ${spec.unit}` : '';
  const open = isOpenEnded(spec.format);
  const hiText = atMax
    ? `${formatRangeEndpoint(max, spec)}${open ? '+' : ''}`
    : formatRangeEndpoint(hi, spec);
  const loText = formatRangeEndpoint(lo, spec);

  if (atMin) return `≤ ${hiText}${unit}`;
  if (atMax) return `≥ ${loText}${unit}`;
  return `${loText} – ${hiText}${unit}`;
}
